<template>
  <div class="account container-fluid">
    <div class="row header-row">
      <div class="col-12 col-lg-6 offset-lg-3">
        <h1>Programmatic Access</h1>
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-lg-6 offset-lg-3">
        <div class="card">
          <h4>Introduction</h4>
          Following these steps you will be able to create an application that will
          be able to do all the functions of the application using HTTPS REST API calls.
          <br>
          The access can be done with either RSA or OpenSSH (ed25519) public/private keys.
          The public keys can be in PEM or SSH format.

          <h4>Setup</h4>
          <ol>
            <li>
              On local computer create a private and public key by running command:
              <br>
              <b-tabs card>
                <b-tab
                  title="Open SSH"
                  active
                >
                  <b-card-text>
                    <pre><code
              class="language-sh"
              data-prismjs-copy="Copy"
                    >ssh-keygen -t ed25519</code></pre>
                  </b-card-text>
                </b-tab>
                <b-tab
                  title="RSA"
                >
                  <b-card-text>
                    <pre><code
              class="language-sh"
              data-prismjs-copy="Copy"
                    >ssh-keygen -t rsa -b 4096</code></pre>
                  </b-card-text>
                </b-tab>
              </b-tabs>

              <br>
              <div
                class="inline-alert"
              >
                Note the location of the file that is generated
                as the private key will be needed for code logic.
              </div>
            </li>
            <li>
              Add/Manage the public key to table below
              <button
                class="btn btn--primary"
                type="button"
                style="float: right;"
                @click="addPublicKey()"
              >
                <font-awesome-icon
                  :icon="['fas', 'plus']"
                  class="icon alt"
                />
                Add Public Key
              </button>

              <responsive-table>
                <thead class="inline-header">
                  <tr>
                    <th>Key</th>
                    <th>Description</th>
                    <th>Last Accessed</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="publicKey of publicKeys"
                    :key="publicKey.id"
                  >
                    <td>{{ publicKey.id }} </td>
                    <td>{{ publicKey.description }} </td>
                    <td>
                      {{ publicKey.lastAccessed ?
                        new Date(publicKey.lastAccessed).toLocaleString() : 'N/A' }}
                    </td>
                    <td class="actions">
                      <button
                        v-b-tooltip.hover
                        class="red-hover"
                        title="Delete"
                        type="button"
                        @click="deletePublicKey(publicKey)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'trash']"
                          class="icon alt"
                        />
                      </button>
                      <button
                        v-b-tooltip.hover
                        class="green-hover"
                        title="Regenerate Access Key"
                        type="button"
                        @click="regenerateAccessKey(publicKey)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'undo']"
                          class="icon alt"
                        />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </responsive-table>
            </li>
            <li>
              Create code to call login endpoint to get bearer auth token to
              access API endpoints. Also in <router-link
                :to="{ name: 'apiDocumentation', hash: '#operation/auth_accessKey_create' }"
              >
                API Documentation
              </router-link>
              <div>
                <div class="method-holder">
                  <b-badge variant="success">
                    POST
                  </b-badge> {{ globalEnv.baseUrl }}/auth/accessKey/
                </div>
                <h5>Parameters</h5>
                <table class="table">
                  <thead>
                    <tr>
                      <th>
                        Location
                      </th>
                      <th>
                        Name
                      </th>
                      <th>
                        Type
                      </th>
                      <th>
                        Required
                      </th>
                      <th>
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        BODY
                      </td>
                      <td>
                        key
                      </td>
                      <td>
                        String
                      </td>
                      <td>
                        TRUE
                      </td>
                      <td>
                        Key id used to login
                      </td>
                    </tr>
                    <tr>
                      <td>
                        BODY
                      </td>
                      <td>
                        accessKey
                      </td>
                      <td>
                        String
                      </td>
                      <td>
                        TRUE
                      </td>
                      <td>
                        Access key used to login
                      </td>
                    </tr>
                    <tr>
                      <td>
                        BODY
                      </td>
                      <td>
                        requestEpochTime
                      </td>
                      <td>
                        Integer
                      </td>
                      <td>
                        TRUE
                      </td>
                      <td>
                        Current epoch time of request
                      </td>
                    </tr>
                    <tr>
                      <td>
                        HEADER
                      </td>
                      <td>
                        x-signature
                      </td>
                      <td>
                        String
                      </td>
                      <td>
                        TRUE
                      </td>
                      <td>
                        The calculated RSA/SSH signature for the message body request.
                        *For RSA use SHA256 hash.
                      </td>
                    </tr>
                  </tbody>
                </table>
                <h5>Sample Request using Python</h5>
                <b-tabs card>
                  <b-tab
                    title="Open SSH"
                    active
                  >
                    <b-card-text>
                      <pre><code
              class="language-py"
              data-prismjs-copy="Copy"
                      >import time
import requests
import base64
import json
from cryptography.hazmat.primitives import serialization

message_body = str(json.dumps({
    'key': 'YOUR_KEY',
    'accessKey': 'YOUR_ACCESS_CODE',
    'requestEpochTime': round(time.time())
}))

with open('your-private-open-ssh-key-file', mode='rb') as private_file:
    key_data = private_file.read()
private_key = serialization.load_ssh_private_key(key_data, 'Passphrase or None')


signature = private_key.sign(bytes(message_body, 'utf-8'))
base_64_signature = base64.b64encode(signature).decode('utf-8')

response = requests.post(
    'http://localhost:8000/auth/accessKey/',
    headers={
        'x-signature': base_64_signature
    },
    data=message_body)

print(response.json())</code></pre>
                    </b-card-text>
                  </b-tab>
                  <b-tab
                    title="RSA"
                  >
                    <b-card-text>
                      <pre><code
              class="language-py"
              data-prismjs-copy="Copy"
                      >import time
import requests
import base64
import json
from cryptography.hazmat.primitives import hashes, serialization
from cryptography.hazmat.primitives.asymmetric import padding

currentEpochTime = round(time.time())
message_body = str(json.dumps({
    'key': 'YOUR_KEY',
    'accessKey': 'YOUR_ACCESS_CODE',
    'requestEpochTime': round(time.time())
}))

with open('your-private-rsa-key-file', mode='rb') as private_file:
    key_data = private_file.read()
private_key = serialization.load_pem_private_key(key_data, 'passphrase or None')

signature = private_key.sign(
    bytes(message_body, 'utf-8'),
    padding.PSS(
        mgf=padding.MGF1(hashes.SHA256()),
        salt_length=padding.PSS.MAX_LENGTH
    ),
    hashes.SHA256()
)
base_64_signature = base64.b64encode(signature).decode('utf-8')
response = requests.post(
    '{{ globalEnv.baseUrl }}/auth/accessKey/',
    headers={
        'x-signature': base_64_signature
    },
    data=message_body)

print(response.json())</code></pre>
                    </b-card-text>
                  </b-tab>
                </b-tabs>
              </div>
            </li>
            <li>
              See <router-link to="/apiDocumentation">
                API Documentation
              </router-link> for all other endpoints
            </li>
          </ol>
        </div>
      </div>
    </div>

    <save-user-access-key-modal
      ref="saveAccessModal"
      :do-save="addKey"
    />
    <!-- Confirmation Modal -->
    <confirm-modal
      ref="confirmModal"
      :title="confirmTitle"
      :body="confirmBody"
      :on-confirm="confirmAction"
    />


    <b-modal
      ref="accessKeyModal"
      :title="confirmTitle"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      ok-only
      ok-title="Close"
      size="lg"
    >
      <div class="warning-text">
        Do not close this window until you have successfully copied the access key.
        The key is stored encrypted and the only way to access the value is on this
        screen or regenerating a new key.
      </div>

      <div>
        {{ confirmBody }}
      </div>

      <table class="table">
        <tr v-if="keyId && keyId.length > 1">
          <th>Key Id</th>
          <td><pre>{{ keyId }}</pre></td>
        </tr>
        <tr>
          <th>Access Key</th>
          <td><pre>{{ accessKey }}</pre></td>
        </tr>
      </table>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import Vue from 'vue';
import Prism from 'prismjs';
import 'prismjs/plugins/toolbar/prism-toolbar';
import 'prismjs/plugins/copy-to-clipboard/prism-copy-to-clipboard';
import UserClient from '@/api/UserClient';
import 'prismjs/themes/prism-tomorrow.min.css';
import 'prismjs/plugins/toolbar/prism-toolbar.min.css';
import 'prismjs/components/prism-python';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import SaveUserAccessKeyModal from '@/components/modals/SaveUserAccessKeyModal.vue';

export default {
  name: 'Account',
  components: {
    ResponsiveTable,
    ConfirmModal,
    SaveUserAccessKeyModal,
  },
  data() {
    const globalEnv = Vue.prototype.$globalEnv;

    return {
      userClient: new UserClient(this.$http),
      globalEnv,
      publicKeys: [],
      confirmTitle: '',
      confirmBody: '',
      confirmAction: undefined,
      keyId: '',
      accessKey: '',
    };
  },
  mounted() {
    window.Prism = window.Prism || {};
    window.Prism.manual = true;
    Prism.highlightAll();
    this.fetchPublicKeys();
  },
  methods: {
    fetchPublicKeys() {
      this.userClient.getUserPublicKeys().then((keys) => {
        this.publicKeys = keys;
      });
    },
    deletePublicKey(publicKey) {
      this.confirmTitle = `Confirm Delete Public Key ${publicKey.id}`;
      this.confirmBody = `Are you sure you want to delete public key ${publicKey.id}? Any application using this key and access key will no longer be able to login.`;
      this.confirmAction = (whenDone) => {
        this.$refs.confirmModal.hide();
        this.userClient.removeUserKey(publicKey.id).then(() => {
          this.publicKeys = this.publicKeys.filter(key => key.id !== publicKey.id);
        });
        whenDone();
      };
      this.$refs.confirmModal.show();
    },
    regenerateAccessKey(publicKey) {
      this.keyId = '';
      this.confirmTitle = `Confirm Regenerate Access Key ${publicKey.id}`;
      this.confirmBody = `Are you sure you want to regenerate access key for public key ${publicKey.id}? Any application using this previous key will no longer be able to login.`;
      this.confirmAction = (whenDone) => {
        this.$refs.confirmModal.hide();
        this.userClient.regenerateUserKey(publicKey.id).then((response) => {
          this.confirmTitle = 'Successfully Regenerated Access Key';
          this.confirmBody = 'The access key was successfully regenerated. Please note your key id and access key.';
          this.accessKey = response.accessKey;
          this.$refs.accessKeyModal.show();
        });
        whenDone();
      };
      this.$refs.confirmModal.show();
    },
    addPublicKey() {
      this.$refs.saveAccessModal.show();
    },
    addKey(newKeyValue) {
      this.userClient.createUserPublicUserKey(newKeyValue).then((response) => {
        this.confirmTitle = 'Successfully Generated Access Key';
        this.confirmBody = 'The access key was successfully generated. Please note your key id and access key.';
        this.keyId = response.id;
        this.accessKey = response.accessKey;
        this.$refs.accessKeyModal.show();
        this.$refs.saveAccessModal.close();
        this.fetchPublicKeys();
      }).catch((e) => {
        this.$refs.saveAccessModal.catchError(e);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  @include border-radius($border-radius);

  margin-bottom: 30px;
  padding: 45px;
  border: solid 1px #ddd;

  @media (max-width: $sm-max) {
    margin-bottom: 20px;
    padding: 30px;
  }

  h4 {
    margin-bottom: 20px;
    font-size: 20px;
  }
}

.inline-alert {
  margin-bottom: 25px;
  padding: 14px;
  color: $blue;
  background: $light-gray;
  border-radius: 10px;
}

.method-holder {
  padding: 14px;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  background: $light-gray;
  border-radius: 10px;
}

.warning-text {
  color: $red;
  font-weight: 900;
}

</style>
